import './IntroPage.scss';
import { PageId } from '../types';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import Spinner from 'react-bootstrap/Spinner';
import tvshows from '../assets/tvshows-sm.webp';
import movies from '../assets/moviees-sm.webp';
import { useNavigate } from 'react-router';
import { useAtomValue, useSetAtom } from 'jotai';
import ContinueWatching from '../components/ContinueWatching';
import { userAtom, userLoginModalOpenAtom } from '@app/atoms/userAtom';
import { loadingProgressItemAtom, localLoadingProgressItemAtom } from '@app/atoms/uiAtoms';
import { useEffect, useState } from 'react';
import { getProgress, requestSubtitlesAutocomplete } from '@app/utils/api';
import { AutocompleteItem, LastWatchedExt } from '@shared/types';
import AutocompleteDropdown from '@app/components/AutocompleteDropdown';
import { useDebounceCallback } from '@app/utils/useDebounceCallback';
import { buildShowIdentifier, isYoutubeShow } from '@shared/utils/show';

interface QuickMetrics {
  activeItems: number;
  rememberedWords: number;
  needReview: number;
}

const IntroPage = () => {
  const navigate = useNavigate();
  const user = useAtomValue(userAtom);
  const setUserLoginModalOpen = useSetAtom(userLoginModalOpenAtom);
  const showLanding = user !== undefined && !user?.email;
  const [quickMetrics, setQuickMetrics] = useState<QuickMetrics | null>(null);
  const [globalQuery, setGlobalQuery] = useState('');
  const [globalAutocomplete, setGlobalAutocomplete] = useState<AutocompleteItem[]>([]);
  const [globalAutocompleteLoading, setGlobalAutocompleteLoading] = useState(false);
  const loadingProgressItem = useAtomValue(loadingProgressItemAtom);
  const localLoadingProgressItem = useAtomValue(localLoadingProgressItemAtom);
  const activeLoadingItem = loadingProgressItem || localLoadingProgressItem;
  const isHomeExtractionLocked = !!activeLoadingItem;
  const homeMetricsV1Enabled = import.meta.env.DEV || import.meta.env.VITE_FEATURE_HOME_METRICS_V1 === 'true';
  const loadingScope: 'series' | 'movies' | 'youtube' | null = (() => {
    if (!activeLoadingItem?.show) {
      return null;
    }
    if (isYoutubeShow(activeLoadingItem.show)) {
      return 'youtube';
    }
    return activeLoadingItem.show.type === 'tvshow' ? 'series' : 'movies';
  })();
  const loadingItemTitle = activeLoadingItem?.show?.originalTitle
    || activeLoadingItem?.show?.title
    || 'current material';
  const loadingItemEpisodeTag = activeLoadingItem?.show?.type === 'tvshow'
    && (activeLoadingItem?.season || activeLoadingItem?.episode)
    ? `S${activeLoadingItem?.season ?? '?'}E${activeLoadingItem?.episode ?? '?'}`
    : '';
  const openCurrentLoadingItem = () => {
    if (!activeLoadingItem?.show) {
      return;
    }
    const show = activeLoadingItem.show;
    const isYoutube = isYoutubeShow(show);
    const targetPage = isYoutube ? PageId.YOUTUBE : (show.type === 'tvshow' ? PageId.SERIES : PageId.MOVIES);
    const showKey = buildShowIdentifier(show);
    if (!showKey) {
      navigate(`/${targetPage}`);
      return;
    }
    navigate(`/${targetPage}/${encodeURIComponent(showKey)}`);
  };

  const mergeAutocomplete = (seriesItems: AutocompleteItem[], movieItems: AutocompleteItem[]) => {
    const seen = new Set<string>();
    const merged: AutocompleteItem[] = [];
    const pushUnique = (item: AutocompleteItem) => {
      const key = `${item.type}|${buildShowIdentifier(item) || item.title}|${item.year || ''}`;
      if (seen.has(key)) {
        return;
      }
      seen.add(key);
      merged.push(item);
    };
    seriesItems.forEach(pushUnique);
    movieItems.forEach(pushUnique);
    return merged.slice(0, 18);
  };

  const handleGlobalSearch = useDebounceCallback(async (queryRaw: string) => {
    const query = queryRaw.trim();
    if (!query.length) {
      setGlobalAutocomplete([]);
      return;
    }

    setGlobalAutocompleteLoading(true);
    try {
      const [seriesItems, movieItems] = await Promise.all([
        requestSubtitlesAutocomplete(PageId.SERIES, query),
        requestSubtitlesAutocomplete(PageId.MOVIES, query),
      ]);
      setGlobalAutocomplete(mergeAutocomplete(seriesItems, movieItems));
    } finally {
      setGlobalAutocompleteLoading(false);
    }
  }, 450, []);

  const handleGlobalSelect = (item: AutocompleteItem) => {
    if (isHomeExtractionLocked) {
      return;
    }
    const showKey = buildShowIdentifier(item);
    const targetPage = item.type === 'tvshow' ? PageId.SERIES : PageId.MOVIES;
    setGlobalAutocomplete([]);
    setGlobalQuery('');
    if (!showKey) {
      navigate(`/${targetPage}`, { state: { resetSearch: true } });
      return;
    }
    navigate(`/${targetPage}/${encodeURIComponent(showKey)}`, {
      state: {
        seedMedia: item,
        forceSeasonPicker: item.type === 'tvshow',
      }
    });
  };

  useEffect(() => {
    if (!user) {
      setQuickMetrics(null);
      return;
    }

    let cancelled = false;
    const loadQuickMetrics = async () => {
      try {
        const PAGE_LIMIT = 40;
        const MAX_PAGES = 3;
        const collected: LastWatchedExt[] = [];
        const seenIds = new Set<string>();

        for (let page = 1; page <= MAX_PAGES; page++) {
          const resp = await getProgress(page, PAGE_LIMIT);
          if (resp?.error) {
            throw new Error(resp.error);
          }
          const pageItems = resp?.list ?? [];

          for (const item of pageItems) {
            const itemId = String(item.id);
            if (seenIds.has(itemId)) {
              continue;
            }
            seenIds.add(itemId);
            collected.push(item);
          }

          if (pageItems.length < PAGE_LIMIT) {
            break;
          }
        }

        const list = collected;
        let activeItems = 0;
        let rememberedWords = 0;
        let needReview = 0;

        for (const item of list) {
          const statuses = item.details?.cardStatuses || {};
          const statusValues = Object.values(statuses);
          const answered = statusValues.length;
          const remembered = statusValues.filter((status) => status === 'remembered').length;
          const hasNotRemembered = statusValues.some((status) => status === 'not_remembered');
          const loaded = Number(item.details?.loadedCardsTotal) || 0;
          const active = Number(item.details?.activeCardsLimit) || 0;
          const total = Math.max(active || loaded, answered);
          if (total > 0 && remembered < total) {
            activeItems += 1;
          }
          rememberedWords += remembered;

          if (hasNotRemembered) {
            needReview += 1;
          }

        }

        if (!cancelled) {
          setQuickMetrics({
            activeItems,
            rememberedWords,
            needReview,
          });
        }
      } catch (err) {
        console.error('Failed to load quick metrics:', err);
        if (!cancelled) {
          setQuickMetrics(null);
        }
      }
    };

    loadQuickMetrics();
    return () => {
      cancelled = true;
    };
  }, [user]);

  if (showLanding) {
    return (
      <div className="intro-landing">
        <div className="intro-landing__hero">
          <div className="intro-landing__badge">Early Access</div>
          <h1>Learn real English from real videos</h1>
          <p>
            KeyLex extracts useful B1+ vocabulary from movies, TV shows, and YouTube,
            then turns it into flashcards with context.
          </p>
          <div className="intro-landing__actions">
            <Button size="lg" onClick={() => setUserLoginModalOpen(true)}>
              Start with email
            </Button>
            <Button
              size="lg"
              variant="outline-secondary"
              onClick={() => navigate(`/${PageId.MOVIES}`, { state: { resetSearch: true, auth: true } })}
            >
              View demo
            </Button>
          </div>
        </div>

        <Row xs={1} md={3} className="g-3 intro-landing__features">
          <Col>
            <Card>
              <Card.Body>
                <Card.Title>Context first</Card.Title>
                <Card.Text>Each word comes with real phrases and timestamps from subtitles.</Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col>
            <Card>
              <Card.Body>
                <Card.Title>Smarter selection</Card.Title>
                <Card.Text>Focus on practical expressions instead of basic textbook vocabulary.</Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col>
            <Card>
              <Card.Body>
                <Card.Title>Progress that sticks</Card.Title>
                <Card.Text>Learn with cards, continue later, and keep progress synced per episode.</Card.Text>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }

  return (
    <div className="home-layout d-flex flex-column align-middle">
      <div className="home-notice" role="status">
        Early access: features and UI may still change.
      </div>

      <section className="home-section home-section--global-search">
        <h2 className="section-title section-title--search">Search TV shows and movies</h2>
        <div className="home-global-search">
          <Form.Group className="home-global-search__group" controlId="homeGlobalSearch">
            <InputGroup>
              <InputGroup.Text className="uk-flag" />
              <Form.Control
                type="text"
                autoComplete="off"
                placeholder="Type a title..."
                value={globalQuery}
                disabled={isHomeExtractionLocked}
                onChange={(event) => {
                  if (isHomeExtractionLocked) {
                    return;
                  }
                  const nextQuery = event.target.value;
                  setGlobalQuery(nextQuery);
                  handleGlobalSearch(nextQuery);
                }}
              />
            </InputGroup>
            {globalAutocompleteLoading && (
              <Spinner
                className="home-global-search__spinner"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                variant="secondary"
              />
            )}
            {!!globalAutocomplete.length && (
              <AutocompleteDropdown autocomplete={globalAutocomplete} onSelect={handleGlobalSelect} />
            )}
          </Form.Group>
          <div className="home-global-search__actions">
            <Button
              variant="outline-secondary"
              size="sm"
              className="home-global-search__youtube-btn"
              disabled={isHomeExtractionLocked}
              onClick={() => navigate(`/${PageId.YOUTUBE}`, { state: { resetSearch: true } })}
            >
              Use YouTube link
            </Button>
          </div>
          {!!activeLoadingItem && (
            <div className="home-loading-lock-note" role="status" aria-live="polite">
              <Spinner animation="border" size="sm" role="status" aria-hidden="true" />
              <div className="home-loading-lock-note__content">
                <div className="home-loading-lock-note__title">
                  Loading words for {loadingItemTitle}{loadingItemEpisodeTag ? ` (${loadingItemEpisodeTag})` : ''}
                </div>
                <div className="home-loading-lock-note__text">New extraction is temporarily locked until this loading finishes.</div>
              </div>
              <Button variant="link" size="sm" className="home-loading-lock-note__link" onClick={openCurrentLoadingItem}>
                Open
              </Button>
            </div>
          )}
        </div>
      </section>

      <section className="home-section home-section--continue continue-watching">
        <Row xs="1" className="w-100">
          <Col>
            <ContinueWatching
              limit={4}
              compact={true}
              title="Continue learning"
            />
          </Col>
        </Row>
      </section>

      <section className="home-section home-section--explore explore-section">
        <h2 className="section-title">Choose your source</h2>
        <Row xs={1} sm={3} className="g-3">
          <Col>
            <Card className="explore-card" onClick={() => navigate(`/${PageId.SERIES}`, { state: { resetSearch: true } })}>
                <Card.Img variant="top" src={tvshows} />
              <Card.Body>
                <Card.Title className="explore-card-title-with-spinner">
                  TV Shows
                  {loadingScope === 'series' && <Spinner animation="border" size="sm" role="status" aria-hidden="true" variant="secondary" />}
                </Card.Title>
                <Card.Text>Pick season and episode</Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col>
            <Card className="explore-card" onClick={() => navigate(`/${PageId.MOVIES}`, { state: { resetSearch: true } })}>
                <Card.Img variant="top" src={movies} />
              <Card.Body>
                <Card.Title className="explore-card-title-with-spinner">
                  Movies
                  {loadingScope === 'movies' && <Spinner animation="border" size="sm" role="status" aria-hidden="true" variant="secondary" />}
                </Card.Title>
                <Card.Text>Extract from full subtitles</Card.Text>
              </Card.Body>
            </Card>
          </Col>
          <Col>
            <Card className="explore-card explore-card--youtube" onClick={() => navigate(`/${PageId.YOUTUBE}`, { state: { resetSearch: true } })}>
              <div className="explore-card-media explore-card-media--youtube" aria-hidden="true">
                <svg className="youtube-logo-mark" viewBox="0 0 90 64" aria-hidden="true" focusable="false">
                  <rect x="3" y="3" width="84" height="58" rx="14" fill="#FF0000" />
                  <polygon points="38,20 38,44 58,32" fill="#FFFFFF" />
                </svg>
              </div>
              <Card.Body>
                <Card.Title className="explore-card-title-with-spinner">
                  YouTube
                  {loadingScope === 'youtube' && <Spinner animation="border" size="sm" role="status" aria-hidden="true" variant="secondary" />}
                </Card.Title>
                <Card.Text>Paste video link</Card.Text>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </section>

      <section className="home-section home-section--metrics quick-metrics-section">
        <div className="home-metrics-header">
          <h2 className="home-metrics-title">{homeMetricsV1Enabled ? 'Quick metrics' : 'Quick actions'}</h2>
          <p className="home-metrics-subtitle">
            {homeMetricsV1Enabled ? 'Your learning snapshot' : 'Focus on what to do next'}
          </p>
        </div>
        <div className="quick-metrics-grid">
          <article className="metric-card metric-card--primary">
            <div className="metric-label">{homeMetricsV1Enabled ? 'Remembered words' : 'In progress'}</div>
            <div className="metric-value">{homeMetricsV1Enabled ? (quickMetrics?.rememberedWords ?? '—') : (quickMetrics?.activeItems ?? '—')}</div>
            <div className="metric-note">
              {homeMetricsV1Enabled
                ? 'Words and phrases marked as remembered.'
                : 'Materials you have started and can continue now.'}
            </div>
            <Button
              size="sm"
              variant="outline-primary"
              className="metric-action-btn"
              onClick={() => navigate(homeMetricsV1Enabled ? '/history' : '/history?mode=in-progress')}
            >
              {homeMetricsV1Enabled ? 'Open history' : 'Continue learning'}
            </Button>
          </article>
          <article className="metric-card">
            <div className="metric-label">Needs review</div>
            <div className="metric-value">{quickMetrics?.needReview ?? '—'}</div>
            <div className="metric-note">Materials where you marked words as not remembered.</div>
            <Button
              size="sm"
              variant="outline-secondary"
              className="metric-action-btn"
              onClick={() => navigate('/history?mode=review')}
            >
              Review difficult
            </Button>
          </article>
        </div>
      </section>
    </div>
  );
};

export default IntroPage;
